import { useState, useCallback, useRef, useEffect } from 'react';
import { useReactRouterAdapter } from './adapters/reactRouter';
import type {
  FilterConfig,
  FilterValue,
  UseUrlFiltersOptions,
  SetFiltersOptions,
} from './types';

//helpers

function inferType(value: FilterValue): string {
  if (Array.isArray(value)) {
    if (value.length === 0 || typeof value[0] === 'string') return 'string[]';
    return 'number[]';
  }
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  return 'string';
}

function defaultDeserialize(
  raw: string | string[],
  type: string,
  defaultValue: FilterValue,
): FilterValue {
  if (type === 'string[]') {
    return Array.isArray(raw) ? raw : [raw];
  }
  if (type === 'number[]') {
    const arr = Array.isArray(raw) ? raw : [raw];
    return arr.map(Number).filter(n => !isNaN(n));
  }
  const str = Array.isArray(raw) ? raw[0] : raw;
  switch (type) {
    case 'number': {
      const n = Number(str);
      return isNaN(n) ? defaultValue : n;
    }
    case 'boolean':
      if (str === 'true') return true;
      if (str === 'false') return false;
      return defaultValue;
    default:
      return str;
  }
}

function defaultSerialize(value: FilterValue): string | string[] {
  if (Array.isArray(value)) return value.map(String);
  return String(value);
}

function buildParamKey(key: string, prefix?: string, separator = '.'): string {
  return prefix ? `${prefix}${separator}${key}` : key;
}

//Hook

//syncs a set of filter values with URL search params
export function useUrlFilters<T extends Record<string, FilterValue>>(
  config: FilterConfig,
  options: UseUrlFiltersOptions = {}
): [T, (updater: T | ((prev: T) => T), setOptions?: SetFiltersOptions) => void] {
  const { prefix, prefixSeparator = '.', replace = true } = options;
  const adapter = useReactRouterAdapter();
  const configRef = useRef(config);
  configRef.current = config;

  const getParamKey = useCallback(
    (key: string) => buildParamKey(key, prefix, prefixSeparator),
    [prefix, prefixSeparator]
  );

  //read state from current URL params
  const readStateFromUrl = useCallback((): T => {
    const params = adapter.get();
    const state: Record<string, FilterValue> = {};

    for (const [key, fieldConfig] of Object.entries(configRef.current)) {
      const paramKey = getParamKey(key);
      const type = fieldConfig.type || inferType(fieldConfig.default);
      const isArray = type === 'string[]' || type === 'number[]';

      const rawValues = params.getAll(paramKey);
      if (rawValues.length === 0) {
        state[key] = fieldConfig.default;
        continue;
      }

      const raw: string | string[] = isArray ? rawValues : rawValues[0];
      const deserialize = fieldConfig.deserialize || ((r: string | string[]) => defaultDeserialize(r, type, fieldConfig.default));
      let value = deserialize(raw) as FilterValue;

      if (!fieldConfig.deserialize && type === 'number' && value === null) {
        value = fieldConfig.default;
      }

      if (fieldConfig.validate) {
        const validated = fieldConfig.validate(value as any);
        value = validated === undefined ? fieldConfig.default : validated;
      }

      state[key] = value;
    }

    return state as T;
  }, [adapter, getParamKey]);

  const [filters, setFiltersInternal] = useState<T>(readStateFromUrl);

  //track whether currently writing to URL to avoid loops
  const writingRef = useRef(false);

  //sync state -> URL
  useEffect(() => {
    const params = new URLSearchParams(adapter.get());
    let changed = false;

    for (const [key, fieldConfig] of Object.entries(configRef.current)) {
      const paramKey = getParamKey(key);
      const value = filters[key];
      const type = fieldConfig.type || inferType(fieldConfig.default);
      const isArray = type === 'string[]' || type === 'number[]';

      const isDefault =
        Array.isArray(value)
          ? (value as any[]).length === 0 ||
            JSON.stringify(value) === JSON.stringify(fieldConfig.default)
          : value === fieldConfig.default ||
            (value === null && fieldConfig.default === null) ||
            value === '';

      if (isDefault) {
        if (params.has(paramKey)) {
          params.delete(paramKey);
          changed = true;
        }
      } else {
        const serialize = fieldConfig.serialize || defaultSerialize;
        const serialized = serialize(value as any);
        const currentValues = params.getAll(paramKey);

        if (isArray && Array.isArray(serialized)) {
          if (JSON.stringify(currentValues) !== JSON.stringify(serialized)) {
            params.delete(paramKey);
            serialized.forEach(v => params.append(paramKey, v));
            changed = true;
          }
        } else {
          const strVal = Array.isArray(serialized) ? serialized[0] : serialized;
          if (currentValues[0] !== strVal) {
            params.set(paramKey, strVal);
            changed = true;
          }
        }
      }
    }

    if (changed) {
      writingRef.current = true;
      adapter.set(params, { replace });
    }
  }, [filters]);

  //sync URL -> state (back/forward navigation)
  useEffect(() => {
    if (writingRef.current) {
      writingRef.current = false;
      return;
    }
    const stateFromUrl = readStateFromUrl();
    setFiltersInternal(prev => {
      const isDifferent = Object.keys(configRef.current).some(key => {
        const a = prev[key];
        const b = stateFromUrl[key];
        if (Array.isArray(a) && Array.isArray(b)) {
          return JSON.stringify(a) !== JSON.stringify(b);
        }
        return a !== b;
      });
      return isDifferent ? stateFromUrl : prev;
    });
  }, [adapter.get()]);

  const setFilters = useCallback(
    (updater: T | ((prev: T) => T), _setOptions?: SetFiltersOptions) => {
      //setOptions.replace could be used for per-call override in future
      //for now the replace behavior is handled at the effect level
      setFiltersInternal(updater);
    },
    []
  );

  return [filters, setFilters];
}
